import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EllipsisOutlined, ExclamationCircleOutlined, FileOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { NewHelpDocumentPage } from './new-help-document-page';
import { NavLink } from 'react-router-dom';
import { apiClient, apiErrorMessage } from '../../utils';
import { HelpDocument, User } from '../../models';
import moment from 'moment';
import { UserContext } from '../contexts';
import { isSuperadmin } from '../../utils';

export function HelpDocumentsPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allHelpDocuments, setAllHelpDocuments] = useState<HelpDocument[]>();
  const [filteredHelpDocuments, setFilteredHelpDocuments] = useState<HelpDocument[]>();
  const [showAll, setShowAll] = useState(false);

  const baseApiUrl = (process.env.REACT_APP_KLT_BASE_API_URL || window.location.protocol + '//' + window.location.host) + '/api/';

  const loadHelpDocuments = useCallback(() => {
    setLoading(true);
    apiClient
      .get('help/documents')
      .then(response => {
        setLoading(false);

        const allDocs = response.data as HelpDocument[];
        setAllHelpDocuments(allDocs);

        if (allDocs) {
          const names: string[] = [];
          for (let i = 0; i < allDocs.length; i++) {
            const name = allDocs[i].name;
            if (!names.some(m => m === name)) {
              names.push(name);
            }
          }
          const filteredDocs = [];
          for (let i = 0; i < names.length; i++) {
            const filteredDoc = allDocs.filter(m => m.name === names[i])[0];
            filteredDocs.push(filteredDoc);
          }
          setFilteredHelpDocuments(filteredDocs);
        }
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadHelpDocuments();
  }, [loadHelpDocuments]);

  const onShowAllToggle = () => {
    setShowAll(!showAll);
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const onRefreshClick = () => {
    loadHelpDocuments();
  };

  const onHelpDocumentAdded = () => {
    closeAddModal();
    loadHelpDocuments();
  };

  const deleteHelpDocument = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`help/documents/${id}`)
      .then(() => {
        setLoading(false);
        loadHelpDocuments();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteHelpDocument = (id: string) => {
    const onOk = () => deleteHelpDocument(id);
    Modal.confirm({
      title: 'Doküman sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Dokümanı silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'del') {
      confirmDeleteHelpDocument(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="edit">
        <NavLink to={'/admin/help-documents/' + id}>Düzenle</NavLink>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, helpDocument: HelpDocument) => (
        <a href={`${baseApiUrl}help/documents/${helpDocument.id}/file`} style={{ fontWeight: 600 }}>
          {name}
        </a>
      ),
    },
    {
      title: 'Revizyon',
      dataIndex: 'revision',
      key: 'revision',
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Ekleme tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => moment(createdAt).format('DD.MM.yyyy HH:mm:ss'),
    },
    {
      title: 'Ekleyen',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: User) => createdBy.fullName,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, helpDocument: HelpDocument) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(helpDocument.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <FileOutlined /> Dokümanlar
          </>
        }
        extra={[
          <>
            {isSuperadmin(user) && (
              <Button key="filter" onClick={onShowAllToggle}>
                {showAll ? 'Son revizyonları göster' : 'Tümünü göster'}
              </Button>
            )}
          </>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {isSuperadmin(user) && (
              <Button key="add" onClick={openAddModal} icon={<PlusOutlined />} type="primary">
                Yeni doküman
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table
          columns={tableColumns}
          dataSource={showAll ? allHelpDocuments : filteredHelpDocuments}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Content>
      <Modal title="Yeni doküman" visible={addModalOpen} footer={null} onCancel={closeAddModal} destroyOnClose={true}>
        <NewHelpDocumentPage onSaved={onHelpDocumentAdded} />
      </Modal>
    </>
  );
}
